import { Card, CardBack } from '@lafayette/ui';
import { useNavigate } from '@tanstack/react-router';
import { useState, type FormEvent } from 'react';

import { createRoom } from '../api.js';
import { readPreferredName, writePreferredName, writeSeat } from '../session.js';
import styles from './LandingPage.module.css';

export function LandingPage() {
  const navigate = useNavigate();
  const [name, setName] = useState(readPreferredName());
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function onSubmit(event: FormEvent): Promise<void> {
    event.preventDefault();
    const trimmed = name.trim();
    if (!trimmed) {
      setError('Enter a name so your friends know which seat is yours.');
      return;
    }
    setBusy(true);
    setError(null);
    try {
      // A ?seed= query makes a deal reproducible for tests; the server ignores
      // it in production.
      const seed = new URLSearchParams(window.location.search).get('seed') ?? undefined;
      const room = await createRoom(trimmed, seed);
      writePreferredName(trimmed);
      writeSeat(room.roomId, {
        playerId: room.playerId,
        resumeToken: room.resumeToken,
        name: trimmed,
      });
      await navigate({ to: '/room/$roomId', params: { roomId: room.roomId } });
    } catch {
      setError('Could not create a room. Try again in a moment.');
      setBusy(false);
    }
  }

  return (
    <main className={styles.page}>
      <form className={styles.panel} onSubmit={onSubmit}>
        <div className={styles.brand}>
          <h1 className={styles.brandTitle}>Lafayette Deal</h1>
          <p className={styles.brandSub}>Lease three strip malls before anyone else.</p>
        </div>

        <div className={styles.gallery} aria-hidden="true">
          <Card definitionId="prop-boba-tea-salon" size="sm" />
          <Card definitionId="money-5" size="sm" />
          <CardBack size="sm" />
        </div>

        <div className={styles.field}>
          <label className={styles.label} htmlFor="name">
            Your name
          </label>
          <input
            id="name"
            className="lf-input"
            value={name}
            onChange={(e) => setName(e.target.value)}
            maxLength={24}
            autoComplete="nickname"
            placeholder="e.g. Mikayla"
          />
        </div>

        {error ? (
          <p className={styles.error} role="alert">
            {error}
          </p>
        ) : null}

        <button className="lf-button lf-button--primary" type="submit" disabled={busy}>
          {busy ? 'Creating…' : 'Create a private room'}
        </button>

        <p className={styles.blurb}>
          You&rsquo;ll get a secret link to share. Anyone with the link can take a seat until you
          lock the lobby. No accounts, no chat &mdash; just the game.
        </p>
      </form>
    </main>
  );
}
